<template>
  <section class="bg-stone-50 py-14 md:py-20">
    <div class="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
      <article class="bg-white border border-gray-200 px-6 py-8 md:px-10 md:py-12">
        <header class="text-center border-b border-gray-200 pb-8 mb-8">
          <h1 class="text-3xl md:text-4xl font-light text-gray-900">
            {{ legalDocument.title }}
          </h1>
          <div class="mt-4 space-y-1 text-sm text-gray-500">
            <p v-for="line in metaLines" :key="line">{{ line }}</p>
          </div>
        </header>

        <div class="space-y-5">
          <template v-for="(block, index) in contentBlocks" :key="`${block.type}-${index}-${block.text}`">
            <h2
              v-if="block.type === 'heading'"
              class="pt-6 border-t border-gray-100 text-xl md:text-2xl font-light text-gray-900"
            >
              {{ block.text }}
            </h2>
            <h3
              v-else-if="block.type === 'subheading'"
              class="pt-3 text-lg font-medium text-gray-900"
            >
              {{ block.text }}
            </h3>
            <p
              v-else-if="block.type === 'notice'"
              class="border border-amber-200 bg-amber-50 px-4 py-3 text-sm font-medium text-amber-900 text-center"
            >
              {{ block.text }}
            </p>
            <p
              v-else-if="block.type === 'end'"
              class="pt-6 text-center text-sm italic text-gray-500"
            >
              {{ block.text }}
            </p>
            <p
              v-else-if="block.type === 'paragraph'"
              class="text-sm md:text-base leading-7 text-gray-700"
            >
              {{ block.text }}
            </p>
            <ul v-else class="list-disc pl-6 space-y-2 text-sm md:text-base leading-7 text-gray-700">
              <li v-for="item in block.items" :key="item">{{ item }}</li>
            </ul>
          </template>
        </div>
      </article>
    </div>
  </section>
</template>

<script setup lang="ts">
import { computed, watchEffect } from 'vue'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { legalDocuments, type LegalDocumentType } from '@/legal/documents'
import type { LocaleCode } from '@/i18n'

type LegalBlock =
  | { type: 'heading' | 'subheading' | 'paragraph' | 'notice' | 'end'; text: string }
  | { type: 'list'; items: string[] }

const route = useRoute()
const { locale } = useI18n()

const documentType = computed<LegalDocumentType>(() => {
  return (route.meta.legalDocument as LegalDocumentType | undefined) || 'privacyPolicy'
})

const legalDocument = computed(() => {
  const currentLocale = locale.value as LocaleCode
  return legalDocuments[documentType.value][currentLocale] || legalDocuments[documentType.value].en
})

const lines = computed(() => {
  return legalDocument.value.rawText
    .split('\n')
    .map((line) => line.trim())
})

const firstHeadingIndex = computed(() => {
  const index = lines.value.findIndex((line) => /^1\.\s/.test(line))
  return index === -1 ? lines.value.length : index
})

const metaLines = computed(() => {
  return lines.value.slice(1, firstHeadingIndex.value).filter(Boolean)
})

const contentBlocks = computed<LegalBlock[]>(() => {
  const blocks: LegalBlock[] = []
  let currentList: string[] = []

  const flushList = () => {
    if (currentList.length > 0) {
      blocks.push({ type: 'list', items: currentList })
      currentList = []
    }
  }

  for (const line of lines.value.slice(firstHeadingIndex.value)) {
    if (!line) {
      flushList()
      continue
    }

    if (line.startsWith('•')) {
      currentList.push(line.replace(/^•\s*/, ''))
      continue
    }

    flushList()

    if (/^\d+\.\s/.test(line)) {
      blocks.push({ type: 'heading', text: line })
    } else if (/^\d+\.\d+\s/.test(line)) {
      blocks.push({ type: 'subheading', text: line })
    } else if (line.startsWith('⚠')) {
      blocks.push({ type: 'notice', text: line })
    } else if (line.startsWith('───')) {
      blocks.push({ type: 'end', text: line })
    } else {
      blocks.push({ type: 'paragraph', text: line })
    }
  }

  flushList()
  return blocks
})

watchEffect(() => {
  window.document.title = `${legalDocument.value.title} - Achiraya Studio`
})
</script>
